import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../context/useAuth'
import { useToast } from '../../context/useToast'
import { useNls } from '../../nls/nlsProvider'
import { Button, StatusChip, AppDialog } from '../Common'
import { WorkspaceItemPicker } from '../Common/WorkspaceItemPicker'
import { isTimesheetsEnabled } from '../../services/featureRegistry'
import {
  subscribeTimesheetSettings,
} from '../../services/timesheetSettingsService'
import { subscribeMyWeekEntries } from '../../services/timesheetsService'
import { useMyClock } from '../../hooks/useMyClock'
import { readCurrentCoordinates } from '../../utils/geolocation'
import { weekIdFor } from '../../utils/timesheets'
import type { Workspace } from '../../types/workspace'
import {
  DEFAULT_TIMESHEET_SETTINGS,
  type TimeEntry,
  type TimesheetSettings,
} from '../../types/timesheets'
import './timesheets.css'

export interface ClockCardProps {
  workspace: Workspace
  actorRole?: 'MEMBER' | 'HELPER'
  className?: string
  testId?: string
}

function formatElapsed(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = totalSeconds % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function formatMinutes(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}

function formatStartTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } catch {
    return iso
  }
}

export const ClockCard = ({
  workspace,
  actorRole = 'MEMBER',
  className = '',
  testId = 'timesheets-clock-card',
}: ClockCardProps) => {
  const { t } = useNls()
  const { currentUser } = useAuth()
  const { showToast } = useToast()
  const uid = currentUser?.uid ?? null

  const isEnabled = isTimesheetsEnabled(workspace)

  const [settings, setSettings] = useState<TimesheetSettings>({
    ...DEFAULT_TIMESHEET_SETTINGS,
  })

  useEffect(() => {
    if (!isEnabled || !workspace?.id) return
    const unsub = subscribeTimesheetSettings(workspace.id, (s) => setSettings(s))
    return unsub
  }, [isEnabled, workspace?.id])

  const { clock, isPending, clockIn, clockOut } = useMyClock({
    workspaceId: workspace?.id ?? '',
    uid,
    actorRole,
  })

  // Selected node for clock-in
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [selectedNodeTitle, setSelectedNodeTitle] = useState<string | null>(null)

  // GPS Consent dialog state
  const [showGpsConsentDialog, setShowGpsConsentDialog] = useState(false)
  const [actionInProgress, setActionInProgress] = useState(false)

  // Live ticking timer
  const [nowMs, setNowMs] = useState(() => Date.now())
  useEffect(() => {
    if (!clock) return
    const interval = setInterval(() => setNowMs(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [clock])

  // Today's total hours from entries
  const [todayEntries, setTodayEntries] = useState<TimeEntry[]>([])
  useEffect(() => {
    if (!isEnabled || !workspace?.id || !uid) return
    const weekId = weekIdFor(new Date(), 'UTC', settings.weekStart)
    const unsub = subscribeMyWeekEntries(workspace.id, uid, weekId, (entries) => {
      const todayIso = new Date().toISOString().slice(0, 10)
      const matchingToday = entries.filter((e) => e.start.slice(0, 10) === todayIso)
      setTodayEntries(matchingToday)
    })
    return unsub
  }, [isEnabled, workspace?.id, uid, settings.weekStart])

  const todayTotalMinutes = useMemo(() => {
    return todayEntries.reduce((sum, e) => sum + (e.minutes || 0), 0)
  }, [todayEntries])

  // Overtime calculation
  const elapsedSeconds = useMemo(() => {
    if (!clock) return 0
    const startMs = new Date(clock.startedAt).getTime()
    return Math.max(0, Math.floor((nowMs - startMs) / 1000))
  }, [clock, nowMs])

  const elapsedMinutes = Math.floor(elapsedSeconds / 60)
  const thresholdMinutes = settings.overtime?.dailyMinutes && settings.overtime.dailyMinutes > 0
    ? settings.overtime.dailyMinutes
    : 12 * 60
  const thresholdHours = Math.round(thresholdMinutes / 60)
  const isOvertime = Boolean(clock && elapsedMinutes >= thresholdMinutes)

  const gpsConsentKey = useMemo(() => {
    return `timesheets:gps-consent:${workspace?.id ?? 'ws'}:${uid ?? 'anon'}`
  }, [workspace?.id, uid])

  const executeClockInWithGeo = useCallback(
    async (withGeo: boolean) => {
      setActionInProgress(true)
      try {
        let geo: { latitude: number; longitude: number; accuracy?: number } | null = null
        if (withGeo) {
          const coords = await readCurrentCoordinates()
          if (coords) {
            geo = { latitude: coords.lat, longitude: coords.lng, accuracy: coords.accuracy }
          } else {
            showToast(t('NLSTimesheetsGpsUnavailableToast'), 'info')
          }
        }
        await clockIn({
          nodeId: selectedNodeId,
          geo,
          device: 'mobile',
        })
        setSelectedNodeId(null)
        setSelectedNodeTitle(null)
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Failed to clock in', 'error')
      } finally {
        setActionInProgress(false)
      }
    },
    [clockIn, selectedNodeId, showToast, t],
  )

  const handleClockInClick = useCallback(() => {
    if (!settings.gps) {
      void executeClockInWithGeo(false)
      return
    }

    let savedConsent: string | null = null
    try {
      savedConsent = window.localStorage.getItem(gpsConsentKey)
    } catch {
      // storage unavailable
    }

    if (savedConsent === 'ACCEPTED') {
      void executeClockInWithGeo(true)
    } else if (savedConsent === 'DECLINED') {
      void executeClockInWithGeo(false)
    } else {
      // First use: prompt with consent dialog
      setShowGpsConsentDialog(true)
    }
  }, [settings.gps, gpsConsentKey, executeClockInWithGeo])

  const handleConsentAccept = useCallback(() => {
    try {
      window.localStorage.setItem(gpsConsentKey, 'ACCEPTED')
    } catch {
      // storage unavailable
    }
    setShowGpsConsentDialog(false)
    void executeClockInWithGeo(true)
  }, [gpsConsentKey, executeClockInWithGeo])

  const handleConsentDecline = useCallback(() => {
    try {
      window.localStorage.setItem(gpsConsentKey, 'DECLINED')
    } catch {
      // storage unavailable
    }
    setShowGpsConsentDialog(false)
    void executeClockInWithGeo(false)
  }, [gpsConsentKey, executeClockInWithGeo])

  const handleClockOutClick = useCallback(async () => {
    setActionInProgress(true)
    try {
      let geo: { latitude: number; longitude: number; accuracy?: number } | null = null
      let savedConsent: string | null = null
      try {
        savedConsent = window.localStorage.getItem(gpsConsentKey)
      } catch {
        // storage unavailable
      }

      if (settings.gps && savedConsent === 'ACCEPTED') {
        const coords = await readCurrentCoordinates()
        if (coords) {
          geo = { latitude: coords.lat, longitude: coords.lng, accuracy: coords.accuracy }
        }
      }
      await clockOut({ geo })
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to clock out', 'error')
    } finally {
      setActionInProgress(false)
    }
  }, [clockOut, settings.gps, gpsConsentKey, showToast])

  if (!isEnabled) {
    return null
  }

  return (
    <div
      className={`timesheetsClockCard ${className}`}
      data-testid={testId}
    >
      <div className="timesheetsClockHeader">
        <span className="timesheetsClockTitle">{t('NLSTimesheetsClockCardTitle')}</span>
        <div className="timesheetsClockBadges">
          {isPending && (
            <span data-testid="clock-waiting-to-sync">
              <StatusChip tone="warning">
                {t('NLSTimesheetsClockWaitingToSync')}
              </StatusChip>
            </span>
          )}
          {Boolean(clock?.geoIn) && (
            <span data-testid="clock-location-captured">
              <StatusChip tone="info">
                {t('NLSTimesheetsLocationCaptured')}
              </StatusChip>
            </span>
          )}
        </div>
      </div>

      <div className="timesheetsClockStatusRow">
        <div>
          {clock ? (
            <>
              <div className="timesheetsClockElapsed" data-testid="clock-elapsed-time">
                {formatElapsed(elapsedSeconds)}
              </div>
              <div className="timesheetsClockSince" data-testid="clock-since-time">
                {t('NLSTimesheetsClockedInSince', { time: formatStartTime(clock.startedAt) })}
              </div>
            </>
          ) : (
            <div className="timesheetsClockSince" data-testid="clock-status-clocked-out">
              {t('NLSTimesheetsClockedOut')}
            </div>
          )}
        </div>

        <div className="timesheetsClockActions">
          {clock ? (
            <Button
              variant="secondary"
              size="md"
              onClick={handleClockOutClick}
              disabled={actionInProgress}
              data-testid="btn-clock-out"
            >
              {t('NLSTimesheetsClockOut')}
            </Button>
          ) : (
            <Button
              variant="primary"
              size="md"
              onClick={handleClockInClick}
              disabled={actionInProgress}
              data-testid="btn-clock-in"
            >
              {t('NLSTimesheetsClockIn')}
            </Button>
          )}
        </div>
      </div>

      {!clock && (
        <div className="timesheetsClockPickerRow" data-testid="clock-node-picker-row">
          <span className="timesheetsClockItemName">
            {selectedNodeTitle ?? t('NLSTimesheetsClockNoItemSelected')}
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setIsPickerOpen(true)}
            data-testid="btn-pick-clock-item"
          >
            {selectedNodeId ? 'Change item' : t('NLSTimesheetsClockInOnItem')}
          </Button>
        </div>
      )}

      {isOvertime && (
        <div className="timesheetsClockHint" data-testid="clock-overtime-hint">
          {t('NLSTimesheetsClockOvertimeHint', { hours: thresholdHours })}
        </div>
      )}

      <div className="timesheetsClockTodayTotal" data-testid="clock-today-total">
        <span>{t('NLSTimesheetsTodayTotal')}</span>
        <span className="timesheetsClockTodayValue" data-testid="clock-today-total-value">
          {formatMinutes(todayTotalMinutes)}
        </span>
      </div>

      {isPickerOpen && (
        <WorkspaceItemPicker
          open={isPickerOpen}
          workspaceId={workspace.id}
          title={t('NLSTimesheetsNodePickerLabel')}
          selectedIds={selectedNodeId ? new Set([selectedNodeId]) : undefined}
          onSelect={(item) => {
            setSelectedNodeId(item ? item.id : null)
            setSelectedNodeTitle(item ? item.label : null)
            setIsPickerOpen(false)
          }}
          onClose={() => setIsPickerOpen(false)}
        />
      )}

      {showGpsConsentDialog && (
        <AppDialog
          open={showGpsConsentDialog}
          onDismiss={() => setShowGpsConsentDialog(false)}
          title={t('NLSTimesheetsGpsConsentTitle')}
          data-testid="dialog-gps-consent"
          message={
            <p className="text-sm text-[var(--text-muted)] leading-relaxed">
              {t('NLSTimesheetsSettingsGpsConsentNote')}
            </p>
          }
          actions={[
            {
              label: t('NLSTimesheetsGpsConsentDecline'),
              variant: 'secondary',
              onClick: handleConsentDecline,
              testId: 'btn-gps-decline',
            },
            {
              label: t('NLSTimesheetsGpsConsentAllow'),
              variant: 'primary',
              onClick: handleConsentAccept,
              testId: 'btn-gps-allow',
            },
          ]}
        />
      )}
    </div>
  )
}
